import styled from '@emotion/styled';
import { useEffect, useState, useRef } from 'react';
import { color, Icon, typography } from '@uniquegood/realworld-studio-design';
import { IPricingPlan, BillingPeriod } from '../../types';
import RecommendBubble from '@/assets/pricing/recommend-bubble.svg';

interface PricingCardProps {
  plan: IPricingPlan;
  billingPeriod: BillingPeriod;
  onCtaClick: (planId: string, billingPeriod?: BillingPeriod) => void;
}

const PricingCard = ({ plan, billingPeriod, onCtaClick }: PricingCardProps) => {
  const price = billingPeriod === 'monthly' ? plan.price.monthly : plan.price.yearly;
  const isHighlighted = plan.highlighted;
  const showDiscount = billingPeriod === 'yearly' && plan.discount?.yearly;
  const [displayPrice, setDisplayPrice] = useState(0);
  const animationIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (price === null) {
      return undefined;
    }

    // 이전 애니메이션 취소
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }

    // 가격이 변경되면 displayPrice 초기화
    setDisplayPrice(0);

    const duration = 1500; // 전체 애니메이션 시간
    const randomChangeSpeed = 10; // 랜덤 숫자 변경 속도 (ms) - 낮을수록 빠름
    const startTime = Date.now();
    let lastChangeTime = startTime;

    // 애니메이션 시작
    const endPrice = price;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // easeOutQuart: 천천히 느려지는 효과
      const easeProgress = 1 - (1 - progress) ** 4;

      // 진행률에 따라 랜덤 범위 좁히기
      const currentTime = Date.now();
      if (currentTime - lastChangeTime >= randomChangeSpeed * (1 + easeProgress * 2)) {
        lastChangeTime = currentTime;

        if (progress < 0.95) {
          // 랜덤한 숫자 표시 (점점 목표에 가까워짐)
          const range = endPrice * (1 - easeProgress);
          const randomOffset = (Math.random() - 0.5) * range;
          const randomPrice = Math.max(0, Math.round(endPrice * easeProgress + randomOffset));
          setDisplayPrice(randomPrice);
        } else {
          // 마지막 5%는 정확한 값으로 수렴
          const currentPrice = Math.round(endPrice * easeProgress);
          setDisplayPrice(currentPrice);
        }
      }

      if (progress < 1) {
        animationIdRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayPrice(endPrice);
        animationIdRef.current = null;
      }
    };

    animationIdRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, [price]);

  const formatPrice = (amount: number) => {
    if (amount === 0) return '₩0';
    return `₩${amount.toLocaleString('ko-KR')}`;
  };

  return (
    <CardContainer isHighlighted={isHighlighted}>
      {plan.badge && (
        <RecommendBadge>
          <img src={RecommendBubble} alt="추천 요금제" />
        </RecommendBadge>
      )}

      <CardHeader>
        {plan.description && <Description>{plan.description}</Description>}
        <PlanNameWrapper>
          <PlanName>{plan.name}</PlanName>
          {plan.discount?.yearly && (
            <DiscountBadge show={!!showDiscount}>{plan.discount.yearly}% 할인!</DiscountBadge>
          )}
        </PlanNameWrapper>
        <PriceSection>
          {price !== null ? (
            <>
              <Price>{formatPrice(displayPrice)}</Price>
              {plan.period && (
                <Period>{billingPeriod === 'yearly' ? '채널당/연' : plan.period}</Period>
              )}
            </>
          ) : (
            <ContactText>도입문의</ContactText>
          )}
        </PriceSection>
      </CardHeader>

      <CtaButton
        onClick={() => onCtaClick(plan.id, billingPeriod)}
        variant={plan.cta.variant}
        planId={plan.id}
      >
        {plan.cta.text}
      </CtaButton>

      <Features>
        {plan.features.map((feature) => (
          <FeatureItem key={feature.text} highlight={feature.highlight}>
            {!feature.highlight && (
              <Icon
                icon="check_solid"
                size="12px"
                color={feature.included ? '#8f8f8f' : '#4A41FF'}
                css={{ margin: 0 }}
              />
            )}
            <FeatureText highlight={feature.highlight} isGradient={feature.isGradient}>
              {feature.text}
              {feature.suffix && <FeatureSuffix>{feature.suffix}</FeatureSuffix>}
            </FeatureText>
          </FeatureItem>
        ))}
      </Features>
    </CardContainer>
  );
};

const CardContainer = styled.div<{ isHighlighted?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${color.white};
  border-radius: 8px;
  padding: 24px;
  width: 260px;
  height: 480px;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.07);
  border: 2px solid transparent;
  transition: transform 0.2s, border-color 0.2s, box-shadow 0.2s;
  margin-top: 30px;

  &:hover {
    transform: translateY(-4px);
    border-color: #7800ff;
    box-shadow: 0 0 30px rgba(0, 0, 0, 0.07), 0 12px 48px rgba(120, 0, 255, 0.4);
  }
`;

const RecommendBadge = styled.div`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;

  img {
    display: block;
    width: auto;
    height: auto;
  }
`;

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const Description = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: #7800ff;
  font-weight: ${typography.weight.regular};
`;

const PlanNameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PlanName = styled.h3`
  font-size: 16px;
  line-height: 24px;
  font-weight: ${typography.weight.bold};
  color: ${color.text_default};
  margin: 0;
`;

const DiscountBadge = styled.span<{ show: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 100px;
  background: #f4f0ff;
  font-size: 12px;
  line-height: 16px;
  font-weight: ${typography.weight.regular};
  color: #7800ff;
  white-space: nowrap;
  opacity: ${({ show }) => (show ? 1 : 0)};
  transform: translateY(${({ show }) => (show ? '0' : '10px')});
  transition: opacity 0.4s ease-out, transform 0.4s ease-out;
`;

const PriceSection = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-top: 0;
`;

const Price = styled.div`
  font-size: 28px;
  font-weight: ${typography.weight.bold};
  color: ${color.text_default};
  line-height: 32px;
`;

const Period = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: #adadad;
`;

const ContactText = styled.div`
  font-size: 28px;
  line-height: 32px;
  font-weight: ${typography.weight.bold};
  color: ${color.text_default};
`;

const CtaButton = styled.button<{ variant: string; planId: string }>`
  width: 100%;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: ${typography.weight.medium};
  line-height: 20px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  margin-bottom: 24px;

  ${({ variant, planId }) => {
    if (variant === 'primary' && (planId === 'enterprise' || planId === 'school')) {
      return `
        background: linear-gradient(90deg, #7800ff 0%, #829bff 100%);
        color: ${color.white};
        &:hover {
          opacity: 0.9;
        }
      `;
    }
    if (variant === 'primary') {
      return `
        background: ${color.interactive_primary_default};
        color: ${color.white};
        &:hover {
          background: ${color.interactive_primary_hovered};
        }
      `;
    }
    if (variant === 'secondary') {
      return `
        background: #e8e8e8;
        color: ${color.text_default};
        &:hover {
          background: #d8d8d8;
        }
      `;
    }
    return `
      background: ${color.white};
      color: ${color.text_default};
      border: 1px solid ${color.border_default_default};
      &:hover {
        background: ${color.surface_default_subdued};
      }
    `;
  }}
`;

const Features = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FeatureItem = styled.li<{ highlight?: boolean }>`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 8px;
  padding: 4px;
`;

const FeatureText = styled.span<{ highlight?: boolean; isGradient?: boolean }>`
  font-size: 12px;
  line-height: 16px;
  color: ${({ highlight }) => (highlight ? color.text_default : color.text_default)};
  font-weight: ${({ highlight }) =>
    highlight ? typography.weight.bold : typography.weight.regular};
  ${({ isGradient }) =>
    isGradient &&
    `
    background: linear-gradient(90deg, rgba(74, 65, 255, 1) 0%, rgba(34, 248, 226, 1) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  `}
`;

const FeatureSuffix = styled.span`
  font-weight: ${typography.weight.regular};
  color: #8f8f8f;
`;

export default PricingCard;
